import React, { useCallback, useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { Stack } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import StatusFilter from './StatusFilter';
import PropertyFilter from './PropertyFilter';
import HouseholdStatusFilter from './HouseholdStatusFilter';
import { selectedStatusesFromSearch } from '../statusFilters';

type Filters = {
  statuses: string[],
  propertyIds: string[],
  householdStatuses: string[],
};

type Props = {|
  onFilterChange: (filters: Filters) => void,
  location: any,
  history: any,
  isPropertyLevel?: boolean,
  propertyId?: string,
  isNeedsHelpFilterShown: boolean,
|};

function FilterBar({
  onFilterChange,
  location,
  history,
  isPropertyLevel = false,
  propertyId,
  isNeedsHelpFilterShown,
}: Props): React$Node {
  // Parse URL parameters on mount and when location changes
  const parseFiltersFromUrl = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);

    return {
      statuses: selectedStatusesFromSearch(
        location.search,
        isNeedsHelpFilterShown,
      ),
      propertyIds: searchParams.getAll('propertyId'),
      householdStatuses: searchParams.getAll('householdStatus'),
    };
  }, [location.search, isNeedsHelpFilterShown]);

  const [filters, setFilters] = useState(() => parseFiltersFromUrl());

  // Sync filters when URL changes (e.g., browser back/forward)
  useEffect(() => {
    const urlFilters = parseFiltersFromUrl();
    setFilters(urlFilters);
    onFilterChange(urlFilters);
  }, [location.search, parseFiltersFromUrl, onFilterChange]);

  // Update URL when filters change, preserving non-filter params (e.g. conversationId)
  const updateUrl = useCallback(
    (newFilters: Filters) => {
      const searchParams = new URLSearchParams(location.search);

      // Clear existing filter params, then re-add
      searchParams.delete('status');
      searchParams.delete('propertyId');
      searchParams.delete('householdStatus');

      if (newFilters.statuses.length === 0) {
        searchParams.append('status', '');
      }
      newFilters.statuses.forEach((status) => {
        searchParams.append('status', status);
      });

      newFilters.propertyIds.forEach((propertyId) => {
        searchParams.append('propertyId', propertyId);
      });

      newFilters.householdStatuses.forEach((householdStatus) => {
        searchParams.append('householdStatus', householdStatus);
      });

      const newSearch = searchParams.toString();
      const newPath = newSearch
        ? `${location.pathname}?${newSearch}`
        : location.pathname;

      history.push(newPath);
    },
    [history, location.pathname, location.search],
  );

  const handleStatusChange = useCallback(
    (statuses: string[]) => {
      const newFilters = {
        ...filters,
        statuses,
      };
      setFilters(newFilters);
      updateUrl(newFilters);
    },
    [filters, updateUrl],
  );

  const handlePropertyChange = useCallback(
    (propertyIds: string[]) => {
      const newFilters = {
        ...filters,
        propertyIds,
      };
      setFilters(newFilters);
      updateUrl(newFilters);
    },
    [filters, updateUrl],
  );

  const handleHouseholdStatusChange = useCallback(
    (householdStatuses: string[]) => {
      const newFilters = {
        ...filters,
        householdStatuses,
      };
      setFilters(newFilters);
      updateUrl(newFilters);
    },
    [filters, updateUrl],
  );

  return (
    <Stack spacing={2} direction="row">
      <StatusFilter
        selectedStatuses={filters.statuses}
        onChange={handleStatusChange}
        isNeedsHelpFilterShown={isNeedsHelpFilterShown}
      />
      {/* Hide property filter when at property level since messages are already scoped */}
      {!isPropertyLevel && (
        <PropertyFilter
          selectedPropertyIds={filters.propertyIds}
          onChange={handlePropertyChange}
        />
      )}
      <HouseholdStatusFilter
        selectedHouseholdStatuses={filters.householdStatuses}
        onChange={handleHouseholdStatusChange}
      />
    </Stack>
  );
}

// $FlowFixMe - withRouter types
export default withRouter(FilterBar);
